import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  Stack,
  Typography,
} from "@mui/material";
import { useAssessmentSession } from "../../contexts/assessmentUserContext";
import { useAssessmentContent } from "../../contexts/assessmentContentContext";
import { useEffect, useState } from "react";
import { exitAssessment } from "../../lib/assessmentExit";
import { useAssessmentEventer } from "../../lib/eventer/useAssessmentEventer";
import { AssessmentLoadingState } from "../../components/AssessmentLoadingState";
import { MicCheckModal } from "../../components/MicCheckModal";
import { resolveLaunchTaskType } from "../../lib/authToken";

type AssessmentWelcomePageProps = {
  testName: "reading" | "listening";
};

export function Welcome({ testName }: AssessmentWelcomePageProps) {
  const { me } = useAssessmentSession();
  const { initializedTests, initializeTest } = useAssessmentContent();
  const trackEvent = useAssessmentEventer();
  const [showMicCheck, setShowMicCheck] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const launchTaskType = resolveLaunchTaskType();

  const studentName = me?.first_name ?? "Student";
  const pageLocation = `${testName}/welcome`;

  useEffect(() => {
    initializeTest(testName, pageLocation).then(() => {
      setIsLoading(false);
    });
  }, [initializeTest, pageLocation, testName]);

  const handleMicCheckSuccess = () => {
    setShowMicCheck(false);
    startAssessment();
  };

  const handleMicCheckClose = () => {
    setShowMicCheck(false);
  };

  const handleMicCheckRetry = () => undefined;

  const goToBackpack = () => {
    setShowMicCheck(false);
    trackEvent({
      action: "assessment_exit_clicked",
      data: {
        source: "welcome",
        testType: testName,
      },
    }).then();
    exitAssessment();
  };

  const navigate = useNavigate();

  const submissionId = initializedTests[testName]?.submissionId;
  const error = initializedTests[testName]?.error;
  const testStatus = initializedTests[testName]?.testStatus?.toLowerCase();
  const isCompleted = testStatus === "completed";
  const welcomeHeader = isCompleted
    ? "You are all done!"
    : `Welcome, ${studentName}`;
  const welcomeMessage = isCompleted
    ? `Thank you for taking our ${testName} experience.`
    : `You are about to begin the ${testName} experience. Your teacher will tell you to start when everyone is ready.`;

  const isDisabled = isCompleted || !submissionId || !!error;

  const startAssessment = () => {
    trackEvent({
      action: "assessment_started",
      data: {
        testType: testName,
        submissionId,
      },
    }).then();
    navigate(`/${testName.toLowerCase()}/${submissionId}`);
  };

  useEffect(() => {
    trackEvent({
      action: "assessment_welcome_page_viewed",
      data: {},
    }).then();
  }, [trackEvent]);

  if (isLoading) return <AssessmentLoadingState />;

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: "flex",
        flexDirection: "column",
        py: { xs: 3, sm: 5 },
        minHeight: "100vh",
        alignContent: "center",
        justifyContent: "space-between",
      }}
    >
      <Stack direction={"column"} alignItems={"center"}>
        <Typography variant={"h4"} color={"primary"}>
          {welcomeHeader}
        </Typography>
        {error ? (
          <Alert
            severity="error"
            sx={{ width: "100%", mb: 3, mt: 3, fontSize: "18px" }}
          >
            {`Uh oh! Something went wrong. Please raise your hand and ask your teacher for help.`}
          </Alert>
        ) : (
          <Typography variant={"body1"}>{welcomeMessage}</Typography>
        )}

        <Box
          component="img"
          src="/images/im-ready.png"
          alt="I'm ready"
          sx={{
            width: { xs: 600, sm: 800, md: 1086 },
            height: { xs: 600, sm: 800, md: 1086 },
            objectFit: "contain",
            maxWidth: "90vw",
            maxHeight: "60vh",
          }}
        />
      </Stack>

      <Stack direction={"row"} justifyContent={"space-between"}>
        <Button onClick={goToBackpack} size="large" variant="outlined">
          Back to Dashboard
        </Button>
        <Button
          onClick={() => {
            trackEvent({
              action: "assessment_mic_check_opened",
              data: {
                testType: testName,
                submissionId,
              },
            }).then();
            setShowMicCheck(true);
          }}
          variant="contained"
          disabled={isDisabled || isCompleted}
          size="large"
        >
          Start
        </Button>
      </Stack>

      <Dialog
        open={showMicCheck}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }

          setShowMicCheck(false);
        }}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: { borderRadius: "24px" },
          },
        }}
      >
        {showMicCheck ? (
          <MicCheckModal
            open={showMicCheck}
            onClose={handleMicCheckClose}
            onSuccess={handleMicCheckSuccess}
            onRetry={handleMicCheckRetry}
            submissionId={submissionId}
            taskType={launchTaskType}
          />
        ) : null}
      </Dialog>
    </Container>
  );
}
